import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus, X } from "lucide-react";
import { DownloadIcon, FilterIcon } from "../../../assets/icon";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { ContactModal } from "../../../components/crm/customer/update-customer";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { TeamDetails } from "../../../components/crm/customer/types";
import LeadDetailsModal from "./lead-details-modal";
import { AssignAgentModal } from "./assign-agent-modal";
import { NewTagModal } from "../../../components/crm/conversation/components/insight";
import Pagination from "../../../components/pagination";
import LeadFiltersPanel, { LeadFiltersState } from "./filters-panel";
import {
  LeadRecord,
  goToLeadConversation,
  LeadTableRow,
  mapRecordToLead,
  recordToContactItem,
  updateLeadSafely,
} from "./shared";

// Assigned leads first, unassigned after — a stable sort (JS's Array.sort
// guarantees this) so it only ever moves leads between the two groups,
// never reorders leads that are already in the same group relative to each
// other, i.e. "should be in order" — each group keeps the backend's own
// sort_order:desc (most recent first) intact.
const byAssignedFirst = (leads: LeadRecord[]): LeadRecord[] =>
  [...leads].sort((a, b) => (a.assignedTo ? 0 : 1) - (b.assignedTo ? 0 : 1));

const AllLeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  // ── A broader sample used only to populate the filter dropdown option
  // lists (channel/tag/status) — there's no dedicated distinct-values
  // endpoint, so this is derived from real data seen so far.
  const [optionLeads, setOptionLeads] = useState<LeadRecord[]>([]);

  // ── Lead Directory table — real backend pagination.
  const [tableLeads, setTableLeads] = useState<LeadRecord[]>([]);
  const [tableInitialLoading, setTableInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingLead, setEditingLead] = useState<LeadRecord | null>(null);
  const [detailsLead, setDetailsLead] = useState<LeadRecord | null>(null);
  const [detailsInitialTab, setDetailsInitialTab] = useState<"Details" | "Insight" | "Actions">("Actions");
  const [teamMembers, setTeamMembers] = useState<TeamDetails[]>([]);
  const [tagLead, setTagLead] = useState<LeadRecord | null>(null);
  const [assignLead, setAssignLead] = useState<LeadRecord | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<LeadFiltersState>({
    startDate: "", endDate: "", channel: "", tag: "", status: "",
  });

  const openLeadDetails = (lead: LeadRecord, tab: "Details" | "Insight" | "Actions" = "Actions") => {
    setDetailsLead(lead);
    setDetailsInitialTab(tab);
  };

  const handleTagsUpdated = (leadId: string, tags: string[]) => {
    setTableLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, tags } : l)));
  };

  const { makeRequest: getOptionLeads } = useCrmApi("/crm/unified", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getTableLeads } = useCrmApi("/crm/unified", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    (async () => {
      const [response] = await getTeamList();
      if (Array.isArray(response)) setTeamMembers(response);
      else if (Array.isArray(response?.users)) setTeamMembers(response.users);
      else if (Array.isArray(response?.results)) setTeamMembers(response.results);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort: stored under `custom.assigned_to` via the same /leads PUT
  // used for tags — not a backend-confirmed field yet. updateLeadSafely
  // fetches the latest lead first since PUT /leads/{id} is a full replace —
  // sending only { custom } would reset score/status/etc. to their defaults.
  const handleAssign = async (lead: LeadRecord, member: TeamDetails | null) => {
    const { ok, error } = await updateLeadSafely(lead.id, { custom: { assigned_to: member?.id ?? null } }, token);
    if (ok) {
      setTableLeads((prev) =>
        byAssignedFirst(prev.map((l) => (l.id === lead.id ? { ...l, assignedTo: member?.id ?? null } : l))),
      );
      toast.success(member ? `Assigned to ${member.first_name} ${member.last_name}` : "Unassigned");
    } else {
      toast.error(error || "Failed to update assignee");
    }
  };

  const handleSaveTagsForLead = async (tags: string[]): Promise<boolean> => {
    if (!tagLead) return false;
    const { ok, error } = await updateLeadSafely(tagLead.id, { tags }, token);
    if (ok) {
      handleTagsUpdated(tagLead.id, tags);
      toast.success("Tags saved");
      return true;
    }
    toast.error(error || "Failed to save tags");
    return false;
  };

  useEffect(() => {
    (async () => {
      const [response, status] = await getOptionLeads(undefined, {
        type: "lead",
        sort_order: "desc",
        page: 1,
        per_page: 100,
      });
      if (status === 200 && response?.results) {
        setOptionLeads(response.results.map(mapRecordToLead));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search before it's sent to the backend as a query param.
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchTableLeads = async (showPaginationLoader = false) => {
    try {
      if (showPaginationLoader) setIsPaginationLoading(true);
      else setTableInitialLoading(true);

      const params: Record<string, unknown> = {
        type: "lead",
        sort_order: "desc",
        page: currentPage,
        per_page: pageSize,
      };
      if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.startDate) params.from_date = appliedFilters.startDate;
      // Confirmed backend-supported query param (2026-07-27) — filters the
      // whole dataset server-side, not just whatever page is loaded.
      if (appliedFilters.tag) params.tag = appliedFilters.tag;

      const [response] = await getTableLeads(undefined, params);
      if (response?.results && Array.isArray(response.results)) {
        let mapped: LeadRecord[] = response.results.map(mapRecordToLead);
        // channel/endDate have no backend query param yet (channel is a
        // heuristic derived field) — filtered client-side on the fetched page,
        // same "frontend protection" tradeoff already used on the Contacts page.
        if (appliedFilters.channel) mapped = mapped.filter((l) => l.channel === appliedFilters.channel);
        if (appliedFilters.endDate) mapped = mapped.filter((l) => !l.updatedAt || l.updatedAt.slice(0, 10) <= appliedFilters.endDate);
        setTableLeads(byAssignedFirst(mapped));
        setTotalPages(Math.max(1, Math.ceil((response.total || 0) / pageSize)));
      } else {
        setTableLeads([]);
        setTotalPages(1);
      }
    } catch {
      setTableLeads([]);
    } finally {
      setTableInitialLoading(false);
      setIsPaginationLoading(false);
    }
  };

  // Initial table fetch
  useEffect(() => {
    fetchTableLeads(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch from the backend whenever page, page size, search or filters change
  useEffect(() => {
    if (!tableInitialLoading) fetchTableLeads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchQuery, appliedFilters]);

  // Jump back to page 1 whenever the search or filters change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, appliedFilters]);

  const channelOptions = useMemo(
    () => Array.from(new Set(optionLeads.map((l) => l.channel).filter(Boolean))) as string[],
    [optionLeads],
  );
  const tagOptions = useMemo(
    () => Array.from(new Set(optionLeads.flatMap((l) => l.tags))),
    [optionLeads],
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(optionLeads.map((l) => l.status).filter(Boolean))) as string[],
    [optionLeads],
  );

  const activeFilterCount = [appliedFilters.startDate, appliedFilters.endDate, appliedFilters.channel, appliedFilters.tag, appliedFilters.status]
    .filter(Boolean).length;

  const clearFilterField = (field: keyof LeadFiltersState) => {
    setAppliedFilters((f) => ({ ...f, [field]: "" }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      {/* Drawers/panels below are deliberately siblings of this space-y-4
          wrapper, not children of it — Tailwind's space-y-* adds a top
          margin to every non-first child, which was shifting their fixed,
          full-screen backdrops down by that same gap. */}
      <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate("/leads")}
          className="p-1 rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft size={16} />
        </button>
        <Link to="/leads" className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">Leads</Link>
        <span className="text-gray-300 dark:text-gray-700">/</span>
        <span className="font-medium text-gray-800 dark:text-gray-200">All Leads</span>
      </div>

      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">All Leads</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Browse, filter, and reach out to potential customers.</p>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <button
            onClick={() => toast.info("Lead export is coming soon.")}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
          >
            Export <DownloadIcon />
          </button>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads by name, phone or email"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20 focus:border-indigo-400"
            />
          </div>
          <button
            onClick={() => setShowAddContact(true)}
            className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
          >
            <Plus size={15} /> Add New Lead
          </button>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 rounded-lg px-3.5 py-2.5 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-500/25 transition-colors whitespace-nowrap"
          >
            <FilterIcon /> Filter
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-indigo-600 text-white text-[10px]">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {appliedFilters.status && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/40 rounded-full px-3 py-1">
                Status: {appliedFilters.status}
                <button onClick={() => clearFilterField("status")} className="bg-transparent border-none cursor-pointer text-indigo-400 dark:text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
              </span>
            )}
            {appliedFilters.tag && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/40 rounded-full px-3 py-1">
                Tags: {appliedFilters.tag}
                <button onClick={() => clearFilterField("tag")} className="bg-transparent border-none cursor-pointer text-indigo-400 dark:text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
              </span>
            )}
            {appliedFilters.channel && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/40 rounded-full px-3 py-1">
                Channel: {appliedFilters.channel}
                <button onClick={() => clearFilterField("channel")} className="bg-transparent border-none cursor-pointer text-indigo-400 dark:text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"><X size={12} /></button>
              </span>
            )}
            {(appliedFilters.startDate || appliedFilters.endDate) && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/40 rounded-full px-3 py-1">
                Date: {appliedFilters.startDate || "…"} – {appliedFilters.endDate || "…"}
                <button
                  onClick={() => setAppliedFilters((f) => ({ ...f, startDate: "", endDate: "" }))}
                  className="bg-transparent border-none cursor-pointer text-indigo-400 dark:text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 p-0 flex items-center"
                ><X size={12} /></button>
              </span>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Contact Name</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Phone</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Source</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Conversation Tags</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Deal Status</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Assignee</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isPaginationLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-10">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                      <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                    </div>
                  </td>
                </tr>
              ) : tableLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                    {activeFilterCount > 0 || debouncedSearchQuery ? "No leads match your filters." : "No leads found yet."}
                  </td>
                </tr>
              ) : (
                tableLeads.map((lead) => (
                  <LeadTableRow
                    key={lead.id}
                    lead={lead}
                    onView={() => goToLeadConversation(navigate, lead)}
                    onEdit={() => setEditingLead(lead)}
                    onOpenDetails={(tab) => openLeadDetails(lead, tab)}
                    onManageTags={setTagLead}
                    teamMembers={teamMembers}
                    onOpenAssign={setAssignLead}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            isLoading={isPaginationLoading}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>
      </div>

      <LeadFiltersPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={appliedFilters}
        channelOptions={channelOptions}
        tagOptions={tagOptions}
        statusOptions={statusOptions}
        onApply={setAppliedFilters}
        onReset={() => setAppliedFilters({ startDate: "", endDate: "", channel: "", tag: "", status: "" })}
      />

      <ReuseableDrawer isOpen={showAddContact} onClose={() => setShowAddContact(false)} title="Add New Lead" initialWidth="30%">
        <ContactModal
          mode="lead"
          variant="drawer"
          isOpen={showAddContact}
          onClose={() => setShowAddContact(false)}
          onCreated={() => {
            setShowAddContact(false);
            fetchTableLeads(true);
          }}
        />
      </ReuseableDrawer>

      <ReuseableDrawer isOpen={!!editingLead} onClose={() => setEditingLead(null)} title="Update Lead" initialWidth="30%">
        <ContactModal
          mode="lead"
          variant="drawer"
          isOpen={!!editingLead}
          onClose={() => setEditingLead(null)}
          contact={editingLead ? recordToContactItem(editingLead) : null}
          onUpdated={() => {
            setEditingLead(null);
            fetchTableLeads(true);
          }}
        />
      </ReuseableDrawer>

      <LeadDetailsModal
        lead={detailsLead}
        initialTab={detailsInitialTab}
        onClose={() => setDetailsLead(null)}
        onTagsUpdated={handleTagsUpdated}
        teamMembers={teamMembers}
      />

      <NewTagModal
        isOpen={!!tagLead}
        onClose={() => setTagLead(null)}
        existingTags={tagLead?.tags || []}
        onSave={handleSaveTagsForLead}
      />

      <AssignAgentModal
        lead={assignLead}
        teamMembers={teamMembers}
        onClose={() => setAssignLead(null)}
        onAssign={handleAssign}
      />
    </div>
  );
};

export default AllLeadsPage;
